import { useState } from 'react'
import { ChevronDown, LogOut, Menu, Users, UserCheck } from 'lucide-react'
import { useAuth } from '@/providers/AuthProvider'
import { PersonaSelectorModal } from '@/components/development/PersonaSelectorModal'

interface HeaderProps {
  onMenuClick?: () => void
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user, logout, isDevelopmentMode, hasActivePersona, clearTestPersona } = useAuth()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showPersonaModal, setShowPersonaModal] = useState(false)

  // NEVER interpolate a null user into the header — `${user?.firstName}` with
  // user === null renders the literal string "undefined undefined" (the
  // 2026-07-14 boot-expiry symptom). While the user is still loading, show a
  // skeleton; once loaded, fall back through name → email, never "undefined".
  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`
  const displayName =
    user?.displayName ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email ||
    ''

  const handleLogout = async () => {
    setShowUserMenu(false)
    await logout()
  }

  return (
    <header className="bg-white border-b border-gray-200 h-16 flex items-center justify-between px-3 sm:px-4 md:px-6">
      {/* Left side - hamburger on mobile */}
      <div className="flex items-center">
        {onMenuClick && (
          <button
            onClick={onMenuClick}
            className="md:hidden p-2 text-gray-500 hover:text-heritage-blue hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        )}
      </div>
      {/* Right side */}
      <div className="flex items-center space-x-2 sm:space-x-4">
        {/* HR Operations cross-app link removed — link will be shared
            directly with HR-ops team rather than surfaced to all users. */}

        {/* User menu */}
        <div className="relative">
          <button 
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center space-x-3 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-primary-700 font-medium">
                {initials}
              </span>
            </div>
            <div className="hidden md:block text-left">
              {user ? (
                <>
                  <p className="text-sm font-medium text-gray-700">{displayName}</p>
                  <p className="text-xs text-gray-500">{user.jobTitle}</p>
                </>
              ) : (
                <>
                  {/* User still resolving (token refresh / slow /auth/me) */}
                  <div className="h-3.5 w-24 bg-gray-200 rounded animate-pulse mb-1" />
                  <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
                </>
              )}
            </div>
            <ChevronDown className="h-4 w-4 text-gray-400" />
          </button>

          {/* Dropdown menu */}
          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50">
              <div className="px-4 py-2 text-sm text-gray-700 border-b">
                <p className="font-medium">{user?.displayName}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
                {hasActivePersona && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center">
                    <Users className="h-3 w-3 mr-1" />
                    Test Persona Active
                  </p>
                )}
              </div>
              
              {/* Use My Profile - Show when persona is active */}
              {hasActivePersona && (
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    clearTestPersona();
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-50 border-b"
                >
                  <UserCheck className="h-4 w-4 mr-2" />
                  Use My Profile
                  <span className="ml-auto text-xs text-emerald-600">Real User</span>
                </button>
              )}
              
              {/* Switch Persona - Only in UAT/Dev */}
              {isDevelopmentMode && (
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    setShowPersonaModal(true);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Switch Persona
                  <span className="ml-auto text-xs text-gray-400">UAT</span>
                </button>
              )}
              
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign out
              </button>
            </div>
          )}

          {/* Click outside to close */}
          {showUserMenu && (
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setShowUserMenu(false)}
            />
          )}
        </div>
      </div>

      {/* Persona Selector Modal */}
      <PersonaSelectorModal 
        open={showPersonaModal}
        onOpenChange={setShowPersonaModal}
      />
    </header>
  )
}
